import {
	ApiResponse,
	ApiState,
	ScheduleManagementCalendarCriteria,
	ScheduleManagementCalendarResults,
	CalendarWindows,
	CalendarRuleset,
	SingleWindowSubmitValues,
	SingleBlockSubmitValues,
	IdResponse,
	RecurrencePatternInfo,
	CalendarWindowSubmitInfo,
	VoidApiState,
	RecurrencePatternValues,
} from 'types';
import { createApiEndpoint, createVoidApiEndpoint, ApiMethod } from './baseApi';

const ApiRoute = {
	GetCalendarSchedule: 'internal/calendarSchedule',
	getAppointmentSummary: (id: string) => `internal/calendarSchedule/appointmentSummary/${id}`,
	getBlockSummary: (id: string) => `internal/calendarSchedule/blockSummary/${id}`,
	CalendarRulesets: (id: number) => `internal/calendar/${id}/calendarRulesets`,
	Window: (id: number) => `internal/calendarSchedule/window/${id}`,
	WindowDetails: (id: number) => `internal/calendarSchedule/window/${id}/details`,
	AddSingleWindow: (id: number) => `internal/calendarSchedule/${id}/window`,
	AddSingleBlock: (id: number) => `internal/calendarSchedule/${id}/block`,
	RecurrencePattern: (id: number) => `internal/calendarSchedule/recurrencePattern/${id}`,
	CalendarRecurrencePattern: (id: number) => `internal/calendarSchedule/${id}/recurrencePattern`,
};

export const createCalendarScheduleService = () => {
	const requestControllers = new Map<string, AbortController>();

	const getCalendarSchedule = async (criteria: ScheduleManagementCalendarCriteria, setState: (result: ApiResponse<ScheduleManagementCalendarResults>) => void): Promise<void> => {
		const api = createApiEndpoint<ScheduleManagementCalendarCriteria>(ApiRoute.GetCalendarSchedule, ApiMethod.POST);
		await api(setState, criteria);
	};

	const getAppointmentSummary = async (id: string, setState: (result: any) => void): Promise<void> => {
		const api = createApiEndpoint<ScheduleManagementCalendarCriteria>(ApiRoute.getAppointmentSummary(id), ApiMethod.GET);
		await api(setState);
	};

	const getBlockSummary = async (id: string, setState: (result: any) => void): Promise<void> => {
		const api = createApiEndpoint(ApiRoute.getBlockSummary(id), ApiMethod.GET);
		await api(setState);
	};

	const getWindowDetails = async (id: number, setState: (result: ApiState<CalendarWindows>) => void): Promise<void> => {
		const api = createApiEndpoint<number>(ApiRoute.WindowDetails(id), ApiMethod.GET);
		await api(setState);
	};

	const getRecurrenceDetails = async (id: number, setState: (result: ApiState<RecurrencePatternInfo>) => void): Promise<void> => {
		const api = createApiEndpoint<number>(ApiRoute.RecurrencePattern(id), ApiMethod.GET);
		await api(setState);
	};

	const getCalendarWindowInfo = async (id: number, setState: (result: ApiState<CalendarWindowSubmitInfo>) => void): Promise<void> => {
		const api = createApiEndpoint<number>(ApiRoute.WindowDetails(id), ApiMethod.GET);
		await api(setState);
	};

	const getCalendarRulesets = async (id: number, setState: (result: ApiState<CalendarRuleset[]>) => void): Promise<void> => {
		const api = createApiEndpoint<number>(ApiRoute.CalendarRulesets(id), ApiMethod.GET);
		await api(setState);
	};

	const addSingleBlock = async (info: SingleBlockSubmitValues, setState: (result: ApiState<IdResponse<number>>) => void): Promise<void> => {
		const api = createApiEndpoint<SingleBlockSubmitValues>(ApiRoute.AddSingleBlock(info.calendarId), ApiMethod.PUT);
		await api(setState, info);
	};

	const addSingleWindow = async (info: SingleWindowSubmitValues, setState: (result: ApiState<IdResponse<number>>) => void): Promise<void> => {
		const api = createApiEndpoint<SingleWindowSubmitValues>(ApiRoute.AddSingleWindow(info.calendarId), ApiMethod.PUT);
		await api(setState, info);
	};

	const addRecurringWindow = async (info: RecurrencePatternValues, calendarId: number, setState: (result: ApiState<IdResponse<number>>) => void): Promise<void> => {
		const api = createApiEndpoint<RecurrencePatternValues>(ApiRoute.CalendarRecurrencePattern(calendarId), ApiMethod.POST);
		await api(setState, info);
	};

	const updateWindow = async (info: RecurrencePatternValues, setState: (result: VoidApiState) => void): Promise<boolean> => {
		const api = createVoidApiEndpoint<RecurrencePatternValues>(ApiRoute.RecurrencePattern(info.idFlexWindowRecurrencePattern), ApiMethod.PUT);
		return await api(setState, info);
	};

	const deleteWindow = async (id: number, setState: (result: VoidApiState) => void): Promise<boolean> => {
		const api = createVoidApiEndpoint<number>(ApiRoute.Window(id), ApiMethod.DELETE);
		return await api(setState);
	};

	const deleteRecurrencePattern = async (id: number, setState: (result: VoidApiState) => void): Promise<boolean> => {
		const api = createVoidApiEndpoint<number>(ApiRoute.RecurrencePattern(id), ApiMethod.DELETE);
		return await api(setState);
	};

	const cleanup = () => {
		requestControllers.forEach((controller) => controller.abort());
		requestControllers.clear();
	};

	return {
		getAppointmentSummary,
		getBlockSummary,
		getCalendarSchedule,
		getCalendarWindowInfo,
		getWindowDetails,
		getRecurrenceDetails,
		getCalendarRulesets,
		addSingleBlock,
		addSingleWindow,
		addRecurringWindow,
		updateWindow,
		deleteWindow,
		deleteRecurrencePattern,
		cleanup,
	};
};

export default createCalendarScheduleService;
